import { Menu, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';

import { Logo } from './logo';

/** What the drawer's Tab wrap considers focusable. Mirrors `Dialog`'s list. */
const DRAWER_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface NavItem {
  key: string;
  label: string;
  /** A Lucide icon at `size={18} strokeWidth={2}`. */
  icon?: ReactNode;
  href?: string;
  active?: boolean;
  onClick?: () => void;
  /** e.g. an unread count. Rendered right-aligned in the muted badge scale. */
  trailing?: ReactNode;
}

export interface NavSection {
  /** Uppercase 11px section heading. Omit for the first, unlabelled group. */
  label?: string;
  items: NavItem[];
}

export interface AppShellProps {
  /** Left nav contents. */
  nav: NavSection[];
  /** Shown at the left of the top bar — the clinic name in admin, "HeyAllergy Console" in super. */
  tenantName: ReactNode;
  /** Right-hand slot of the top bar: feedback link, version, user menu (FR-UI-05). */
  userMenu?: ReactNode;
  /** Full-width strip above the top bar, e.g. the impersonation banner (ADR-026). */
  banner?: ReactNode;
  /** Footer under the content, usually a VersionBadge. */
  footer?: ReactNode;
  /** `tenant` reads `--logo-url`; the super admin app passes `platform`. */
  logoVariant?: 'platform' | 'tenant';
  logoLabel?: string;
  /**
   * Renders a nav item's anchor. Supply the router's Link here so the shell stays
   * router-agnostic; the default is a plain `<a>`.
   */
  renderLink?: (item: NavItem, props: LinkRenderProps) => ReactNode;
  children: ReactNode;
}

export interface LinkRenderProps {
  className: string;
  'aria-current': 'page' | undefined;
  children: ReactNode;
}

/**
 * `max-lg:min-h-11` is the drawer's tap target (rules §49.3, WCAG 2.5.5): a 13px item
 * with 10px of padding is 40px tall, which is fine under a mouse and small under a
 * thumb. It grows only while the nav is the off-canvas drawer, so the desktop rhythm
 * of the tokens doc §10.1 is untouched.
 */
const ITEM_BASE =
  'group flex w-full items-center gap-[10px] rounded-input px-3 py-[10px] max-lg:min-h-11 text-sm font-medium transition-colors duration-150 text-left';

/**
 * The admin chrome (FR-UI-05, tokens doc §10.1–§10.2): a **white** 240px left nav with
 * the logo at the top, a white 56px top bar, and the content on `--color-canvas`.
 *
 * The nav is white, not dark, for two reasons from the token extraction: the only logo
 * asset is full-color on white, and the funnel's whole identity is white surfaces on
 * `#F8FAFC`. The active item reuses the funnel's *selected appointment type* recipe —
 * tinted `--color-nav-active-bg` with `--color-primary-accessible` text and a 3px blue
 * accent bar — so the solid blue fill stays reserved for the one primary action and
 * "selected nav item" never competes with "primary button". (Q-P16; the ink is the
 * accessible sibling of `--color-nav-active-ink`, see `NavItemView`.)
 *
 * Below 1024px the nav becomes an off-canvas drawer behind a menu button; critical
 * actions stay reachable at every width (rules §49.3, §49.5).
 *
 * **M11.11.** A closed drawer is `invisible` below `lg`, not merely translated off
 * screen: `visibility: hidden` takes it out of the tab order, so Tab from the menu
 * button no longer walks fourteen links the user cannot see. It is a media-query'd
 * class rather than `inert`, because the same element is the permanent desktop nav and
 * must stay focusable there with no JavaScript deciding which. Opening it moves focus
 * to the close button and Escape closes it, which is what a drawer over the page owes
 * a keyboard (rules §48.2, §48.7).
 */
export function AppShell({
  nav,
  tenantName,
  userMenu,
  banner,
  footer,
  logoVariant = 'tenant',
  logoLabel,
  renderLink,
  children,
}: AppShellProps) {
  const [open, setOpen] = useState(false);
  const navId = useId();
  const mainId = useId();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);

  /* Focus follows the drawer in and back out again. */
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    openerRef.current?.focus();
  }, []);

  /**
   * M13.7 (WCAG 2.4.3 Focus Order). While the drawer is the off-canvas panel it is a
   * modal — it sits over the page behind a scrim — so Tab has to stay inside it. The
   * closed drawer is already `invisible` and therefore out of the tab order; what was
   * missing is the other direction: tabbing past the last nav item walked straight into
   * the content the scrim is covering, with the focus ring somewhere the user cannot
   * see. The wrap mirrors `Dialog`'s, and it is installed only while `open`, so the
   * permanent desktop nav is untouched.
   */
  const onDrawerKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && open) {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab' || !open) return;

      const drawer = drawerRef.current;
      if (drawer === null) return;
      /* No visibility filter: the only focusables in the drawer are the close button and
         the nav links, and this handler is installed only while the drawer is open —
         which is only ever below `lg`, where all of them are on screen. (`offsetParent`
         would have been the obvious filter and is exactly wrong: jsdom has no layout, so
         it reports null for everything and the trap would be dead in the test suite and
         alive in the browser — the worst of both.) */
      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [close, open],
  );

  return (
    <div className="min-h-screen bg-canvas">
      {/*
       * M13.7 (WCAG 2.4.1 Bypass Blocks). Fourteen navigation links stand between the
       * top of every page and its content; without this a keyboard user walks all of
       * them on every navigation. `sr-only` until it is focused, then a normal button at
       * the top-left — `focus:` undoes the whole clip, so it is the first thing Tab
       * reaches and the first thing a sighted keyboard user sees.
       */}
      <a
        href={`#${mainId}`}
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:inline-flex focus:min-h-11 focus:items-center focus:rounded-button focus:bg-surface focus:px-4 focus:text-sm focus:font-semibold focus:text-primary-accessible focus:shadow-dropdown"
      >
        Skip to main content
      </a>
      {banner === undefined ? null : <div className="w-full">{banner}</div>}

      <div className="flex min-h-screen">
        {/* ---- left navigation ---- */}
        <div
          ref={drawerRef}
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-[var(--container-nav)] shrink-0 border-r border-neutral-200 bg-nav-surface transition-transform duration-200',
            'lg:sticky lg:top-0 lg:h-screen lg:visible lg:translate-x-0',
            open ? 'translate-x-0' : 'invisible -translate-x-full',
          )}
          id={navId}
          {...(open ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Main navigation' } : {})}
          onKeyDown={onDrawerKeyDown}
        >
          <div className="flex h-14 items-center justify-between border-b border-neutral-200 px-6">
            <Logo
              variant={logoVariant}
              height={26}
              {...(logoLabel === undefined ? {} : { label: logoLabel })}
            />
            <button
              ref={closeRef}
              type="button"
              className="-mr-2 flex size-11 cursor-pointer items-center justify-center rounded-input text-neutral-400-text hover:bg-neutral-50 lg:hidden"
              onClick={close}
              aria-label="Close navigation"
            >
              <X size={18} strokeWidth={2} aria-hidden={true} />
            </button>
          </div>

          <nav aria-label="Main" className="flex flex-col gap-6 overflow-y-auto p-3">
            {nav.map((section, sectionIndex) => (
              <div key={section.label ?? `section-${String(sectionIndex)}`}>
                {section.label === undefined ? null : (
                  <div className="px-3 pb-2 text-2xs font-medium tracking-badge text-neutral-400-text uppercase">
                    {section.label}
                  </div>
                )}
                <ul className="flex flex-col gap-[2px]">
                  {section.items.map((item) => (
                    <li key={item.key} className="relative">
                      <NavItemView item={item} renderLink={renderLink} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* Scrim behind the mobile drawer. A convenience for a pointer only, so it
            claims no role and no name (M11.11): as a <button> it announced a second
            "Close navigation" control that a keyboard already had, and the real
            affordances — the close button and Escape — are both inside the drawer.
            Same reasoning as `Dialog`'s backdrop. */}
        {open ? (
          <div
            aria-hidden="true"
            data-testid="nav-scrim"
            className="fixed inset-0 z-30 bg-ink/30 lg:hidden"
            onMouseDown={close}
          />
        ) : null}

        {/* ---- content column ---- */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-neutral-200 bg-surface px-6 max-[480px]:px-4">
            <button
              ref={openerRef}
              type="button"
              className="-ml-2 flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-input text-neutral-500 hover:bg-neutral-50 lg:hidden"
              onClick={() => {
                setOpen(true);
              }}
              aria-label="Open navigation"
              aria-expanded={open}
              aria-controls={navId}
            >
              <Menu size={18} strokeWidth={2} aria-hidden={true} />
            </button>
            <div className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
              {tenantName}
            </div>
            {userMenu === undefined ? null : (
              <div className="flex shrink-0 items-center gap-4">{userMenu}</div>
            )}
          </header>

          {/* `tabIndex={-1}` so the skip link's jump actually moves focus and not just
              the scroll position; `focus:outline-none` is deliberately NOT set, so the
              landing is visible (M13.7). */}
          <main id={mainId} tabIndex={-1} className="flex-1 p-6 max-[480px]:px-4 max-[480px]:py-5">
            {children}
          </main>

          {footer === undefined ? null : (
            <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 px-6 py-4 text-xs text-neutral-400-text max-[480px]:px-4">
              {footer}
            </footer>
          )}
        </div>
      </div>
    </div>
  );
}

interface NavItemViewProps {
  item: NavItem;
  renderLink: AppShellProps['renderLink'];
}

function NavItemView({ item, renderLink }: NavItemViewProps) {
  const active = item.active === true;
  const className = cn(
    ITEM_BASE,
    active
      ? /* M11.11: `--color-nav-active-ink` is 4.10:1 on `--color-nav-active-bg`, which
           is short of AA for the one item that says where you are. The tint is kept —
           the recipe is the funnel's selected appointment type — and only the ink moves
           to `--color-primary-accessible` (5.11:1 on that tint). Token values unchanged. */
        'bg-nav-active-bg font-semibold text-primary-accessible'
      : 'text-neutral-700 hover:bg-neutral-50',
  );

  const body = (
    <>
      {active ? (
        <span
          aria-hidden="true"
          className="absolute top-2 bottom-2 left-0 w-[3px] rounded-r-sm bg-primary"
        />
      ) : null}
      {item.icon === undefined ? null : (
        <span
          className={cn('shrink-0', active ? 'text-primary-accessible' : 'text-neutral-400-text')}
        >
          {item.icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.trailing ?? null}
    </>
  );

  if (item.href !== undefined) {
    if (renderLink !== undefined) {
      return renderLink(item, {
        className,
        'aria-current': active ? 'page' : undefined,
        children: body,
      });
    }
    return (
      <a href={item.href} className={className} aria-current={active ? 'page' : undefined}>
        {body}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={cn(className, 'cursor-pointer')}
      aria-current={active ? 'page' : undefined}
      onClick={item.onClick}
    >
      {body}
    </button>
  );
}
